import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ProductCapabilities from './components/ProductCapabilities';
import AIExperiencePage from './components/AIExperiencePage';
import SoundGenerationIntro from './components/SoundGenerationIntro';
import SoundEditingIntro from './components/SoundEditingIntro';
import { LanguageProvider } from './contexts/LanguageContext';
import DevPanel from './components/DevPanel';
import { trackVisit } from './utils/visitorTracker';
import { trackEvent } from './utils/gtag';

export default function App() {
  const [experienceTab, setExperienceTab] = useState<string | null>(null);
  const [introPage, setIntroPage] = useState<string | null>(null);
  const [showDevPanel, setShowDevPanel] = useState(false);

  // Track visit once on mount
  useEffect(() => {
    trackVisit();
  }, []);


  const gotoTry = (tabId: 'gen' | 'edit' | 'gen_tryNow' | 'edit_tryNow') => {
    // Hash-based routing for distinct URLs (try-gen / try-edit)
    const isGen = tabId === 'gen' || tabId === 'gen_tryNow';
    trackEvent('navigate', { page: isGen ? 'try-gen' : 'try-edit' });
    window.location.hash = isGen ? 'try-gen' : 'try-edit';
    setIntroPage(null);
    setExperienceTab(isGen ? 'gen_tryNow' : 'edit_tryNow');
  };
  const gotoLearn = (tabId: 'gen' | 'edit') => {
    trackEvent('navigate', { page: `learn-${tabId}` });
    if (tabId === 'gen') {
      window.location.hash = 'learn-gen';
    } else if (tabId === 'edit') {
      window.location.hash = 'learn-edit';
    }
    setExperienceTab(null);
    setIntroPage(tabId);
  };
  const gotoHome = () => {
    trackEvent('navigate', { page: 'home' });
    // Clear hash to return to base URL
    window.location.hash = '';
    setExperienceTab(null);
    setIntroPage(null);
  };

  // Hidden keyboard shortcut: Ctrl + Shift + D → toggle developer panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDevPanel((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    // Hash-based routing: sync state with URL hash on load and when hash changes
    const applyHashRoute = () => {
      const h = (window.location.hash || '').replace(/^#/, '');
      switch (h) {
        case 'try-gen': setIntroPage(null); setExperienceTab('gen_tryNow'); break;
        case 'try-edit': setIntroPage(null); setExperienceTab('edit_tryNow'); break;
        case 'learn-gen': setExperienceTab(null); setIntroPage('gen'); break;
        case 'learn-edit': setExperienceTab(null); setIntroPage('edit'); break;
        default: setExperienceTab(null); setIntroPage(null); break;
      }
    };
    applyHashRoute();
    const onHashChange = () => applyHashRoute();
    window.addEventListener('hashchange', onHashChange);

    // Also expose via console for easy access
    (window as any).showDevPanel = () => setShowDevPanel(true);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  if (experienceTab) {
    return (
      <LanguageProvider>
        {showDevPanel && <DevPanel onClose={() => setShowDevPanel(false)} />}
        <AIExperiencePage
          onBack={() => gotoHome()}
          initialTab={experienceTab}
        />
      </LanguageProvider>
    );
  }

  if (introPage === 'gen') {
    return (
      <LanguageProvider>
        {showDevPanel && <DevPanel onClose={() => setShowDevPanel(false)} />}
        <SoundGenerationIntro
          onBack={() => gotoHome()}
          onNavigateToExperience={(tabId) => {
            setIntroPage(null);
            setExperienceTab(tabId);
          }}
        />
      </LanguageProvider>
    );
  }
  if (introPage === 'edit') {
    return (
      <LanguageProvider>
        {showDevPanel && <DevPanel onClose={() => setShowDevPanel(false)} />}
        <SoundEditingIntro
          onBack={() => gotoHome()}
        />
      </LanguageProvider>
    );
  }
  return (
    <LanguageProvider>
      {showDevPanel && <DevPanel onClose={() => setShowDevPanel(false)} />}
      <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-blue-100">
        <Navbar />
        <main className="pt-20">
          <Hero onEnterExperience={() => gotoTry('gen')} />
          <ProductCapabilities
            onNavigateToExperience={(tabId) => setExperienceTab(tabId)}
            onNavigateToIntro={(tabId) => setIntroPage(tabId)}
          />
        </main>
      </div>
    </LanguageProvider>
  );
}
